import { featureFlagsApi } from '@/lib/api';
import { type EnumDef, FLAG_DEFS } from '@mss_creator/shared';
import { Alert, Button } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { SettingSection } from './setting-section';
import { StandardFlagRow } from './standard-flag-row';
import { TahitiFlagRow } from './tahiti-flag-row';
import { FEATURE_FLAGS_QUERY_KEY } from './use-flag-mutation';

const TAHITI_KEY = 'tahiti-environment';

function isEnumDef(def: (typeof FLAG_DEFS)[number]): def is EnumDef {
  return def.type === 'enum';
}

function FlagsSkeleton() {
  return (
    <output className="block" aria-label="Chargement des réglages globaux">
      {FLAG_DEFS.map((def) => (
        <div
          key={def.key}
          className="flex flex-col gap-2 border-gray-200 border-t py-4"
          aria-hidden="true"
        >
          <span className="block h-3.5 w-56 rounded-sm bg-gray-100" />
          <span className="block h-3 w-full max-w-[520px] rounded-sm bg-gray-100" />
          <span className="block h-3 w-full max-w-[380px] rounded-sm bg-gray-100" />
          <span className="mt-1 block h-14 w-60 rounded-md bg-gray-100" />
        </div>
      ))}
    </output>
  );
}

/**
 * The deployment-wide section, shown to a real `pms_admin` only: the three
 * backend flags. Skeleton rows while `GET /feature-flags` runs; if it fails,
 * the section says so and offers a retry (the browser-scoped section above it
 * keeps working, it never depends on the server).
 */
export function FlagsSection() {
  const flagsQuery = useQuery({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: featureFlagsApi.list,
  });

  return (
    <SettingSection
      title="Tout le déploiement"
      where="S'applique tout de suite à tous les utilisateurs de cet environnement."
    >
      {flagsQuery.isPending ? (
        <FlagsSkeleton />
      ) : flagsQuery.isError ? (
        <div className="mt-3">
          <Alert
            tone="danger"
            title="Impossible de charger les réglages globaux"
            action={
              <Button variant="primary" size="sm" onClick={() => void flagsQuery.refetch()}>
                Réessayer
              </Button>
            }
          >
            Vérifiez votre connexion, puis réessayez. Les réglages de ce navigateur restent
            disponibles.
          </Alert>
        </div>
      ) : (
        FLAG_DEFS.filter(isEnumDef).map((def) => {
          const serverValue =
            flagsQuery.data.find((flag) => flag.key === def.key)?.value ?? def.default;
          return def.key === TAHITI_KEY ? (
            <TahitiFlagRow key={def.key} def={def} serverValue={serverValue} />
          ) : (
            <StandardFlagRow key={def.key} def={def} serverValue={serverValue} />
          );
        })
      )}
    </SettingSection>
  );
}
